import React, { FC, useEffect, useState } from 'react';
import {
  EditButton,
  ShowButton,
  useDataProvider,
  useNotify,
  usePermissions,
} from 'react-admin';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import DialogContent from '@material-ui/core/DialogContent';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import Dialog from '@material-ui/core/Dialog';

import { getFieldByTitle } from '../../helpers/sortByTitle';
import { User, UserPhone } from '../../types/user';
import UsersListTittle from '../common/listTitles/UsersListTitle';
import HeadTable from '../common/sortTable/sortTable';
import PaginationTable from '../common/paginationTable';
import DiscountFilter from '../fixedOdds/DiscountFilter';
import RemoveItem from '../common/Remove/RemoveItem';
import TablesSearch from '../common/tablesSearch/tablesSearch';
import { checkPermission } from '../../helpers/checkPermissions';

import './index.css';

const UsersList: FC = () => {
  const { permissions } = usePermissions();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  useEffect(() => {
    getUsers('all', 1, 10, '_id', 'asc', '', '', '', '');
  }, []);

  const [state, setState] = useState({
    users: Array<User>(),
    total: 0,
    page: 1,
    perPage: 10,
    field: '_id',
    order: 'asc',
  });

  const [dropdown, setDropdown] = useState({
    first: false,
    last: false,
    email: false,
    phone: false,
    status: false,
  });

  const [filter, setFilter] = useState({
    blocked: 'all',
    searchField: '',
  });

  const [searchState, setSearchState] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
  });

  const [blockedFilter, setBlockedFilter] = useState({
    with: false,
    without: false,
  });

  const [modal, setModal] = useState({
    showModal: false,
    id: '',
  });

  const getUsers = async (
    blocked: string,
    page: number,
    perPage: number,
    field: string,
    order: string,
    searchName: string,
    searchSurname: string,
    searchEmail: string,
    searchPhone: string
  ) => {
    const users = (await dataProvider.getList('users', {
      filter: {
        blocked: blocked,
        searchName: searchName,
        searchSurname: searchSurname,
        searchEmail: searchEmail,
        searchPhone: searchPhone,
      },
      pagination: { page: page, perPage: perPage },
      sort: { field: field, order: order },
    })) as any;
    setState({
      ...state,
      users: users.data,
      total: users.total,
      page: page,
      perPage: perPage,
      field: field,
      order: order,
    });
  };

  const checkBlockedFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    const discount = blockedFilter;
    discount[event.target.name] = event.target.checked;
    let currentDiscount = '';
    if (
      (discount.with && discount.without) ||
      (!discount.with && !discount.without)
    ) {
      currentDiscount = 'all';
    }
    if (discount.with && !discount.without) {
      currentDiscount = 'with';
    }
    if (!discount.with && discount.without) {
      currentDiscount = 'without';
    }
    setFilter({ ...filter, blocked: currentDiscount });
    setBlockedFilter(discount);
  };

  const search = (value: string, field: string) => {
    setSearchState({ ...searchState, [field]: value });
  };

  const clearSearch = (clear: string) => {
    setSearchState({ ...searchState, [clear]: '' });
  };

  const filterDropdown = (title: string) => {
    const titleArray = title.toLowerCase().split(' ');
    setDropdown({ ...dropdown, [titleArray[0]]: !dropdown[titleArray[0]] });
  };

  const handleSort = (direction: string, title: string) => {
    const field = getFieldByTitle(title);
    getUsers(
      filter.blocked,
      state.page,
      state.perPage,
      field,
      direction,
      searchState.name,
      searchState.surname,
      searchState.email,
      searchState.phone
    );
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const perPage = parseInt(event.target.value, 10);
    getUsers(
      filter.blocked,
      1,
      perPage,
      state.field,
      state.order,
      searchState.name,
      searchState.surname,
      searchState.email,
      searchState.phone
    );
  };

  const handleLastPage = () => {
    const page = Math.ceil(state.total / state.perPage);
    getUsers(
      filter.blocked,
      page,
      state.perPage,
      state.field,
      state.order,
      searchState.name,
      searchState.surname,
      searchState.email,
      searchState.phone
    );
  };

  const handleFirstPage = () => {
    getUsers(
      filter.blocked,
      1,
      state.perPage,
      state.field,
      state.order,
      searchState.name,
      searchState.surname,
      searchState.email,
      searchState.phone
    );
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    getUsers(
      filter.blocked,
      newPage + 1,
      state.perPage,
      state.field,
      state.order,
      searchState.name,
      searchState.surname,
      searchState.email,
      searchState.phone
    );
  };

  const applySearch = (field: string) => {
    setFilter({ ...filter, searchField: field });
    getUsers(
      filter.blocked,
      1,
      10,
      '_id',
      'asc',
      searchState.name,
      searchState.surname,
      searchState.email,
      searchState.phone
    );
  };

  const clearDiscountFilter = () => {
    setBlockedFilter({ with: false, without: false });
    setFilter({ ...filter, blocked: 'all' });
  };

  const changeStatus = async (user: User) => {
    user.isBlocked = !user.isBlocked;
    await dataProvider.update('users/cms', {
      data: user,
      id: user.id,
      previousData: {} as User,
    });
    getUsers(
      filter.blocked,
      state.page,
      state.perPage,
      state.field,
      state.order,
      searchState.name,
      searchState.surname,
      searchState.email,
      searchState.phone
    );
  };

  const applyBlockedFilter = () => {
    let blocked = '';
    if (
      (blockedFilter.with && blockedFilter.without) ||
      (!blockedFilter.with && !blockedFilter.without)
    ) {
      blocked = 'all';
    }
    if (blockedFilter.with && !blockedFilter.without) {
      blocked = 'with';
    }
    if (!blockedFilter.with && blockedFilter.without) {
      blocked = 'without';
    }
    setFilter({ ...filter, blocked: blocked });
    getUsers(
      blocked,
      1,
      state.perPage,
      state.field,
      state.order,
      searchState.name,
      searchState.surname,
      searchState.email,
      searchState.phone
    );
  };

  const closeModal = () => {
    setModal({ ...modal, showModal: false, id: '' });
  };

  const openModal = (id: string) => {
    setModal({ ...modal, showModal: true, id: id });
  };

  const removeUser = async () => {
    try {
      const result = await dataProvider.delete('users', {
        id: modal.id,
        previousData: {} as User,
      });
      if (result.data && result.data.message) {
        closeModal();
        notify(result.data.message, 'info');
        getUsers(
          filter.blocked,
          state.page,
          state.perPage,
          state.field,
          state.order,
          searchState.name,
          searchState.surname,
          searchState.email,
          searchState.phone
        );
      }
    } catch (error) {
      notify('User not found', 'error');
    }
  };

  const closeFilter = (e: any) => {
    setDropdown({
      email: false,
      first: false,
      last: false,
      phone: false,
      status: false,
    });
  };

  const parsePhone = (phone: string | UserPhone) => {
    try {
      if (!phone) return '';

      if (typeof phone === 'string') {
        return phone.trim();
      }

      if (typeof phone === 'object') {
        const { countryCode, number } = phone;

        if (!countryCode && !number) return '';
        if (!countryCode && number) return number.trim();
        if (countryCode && !number) return '';

        return `${countryCode}${number}`.trim();
      }

      return '';
    } catch (error) {
      console.error('Error for get user full phone by tools:', error);
      return '';
    }
  };

  return (
    <>
      <UsersListTittle />
      <Box marginTop='0.5em'>
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell id='active-table-head'>
                    <HeadTable
                      title='First Name'
                      sort={handleSort}
                      filterHandle={filterDropdown}
                    />
                    {dropdown.first && (
                      <TablesSearch
                        search={(event) =>
                          search(event.currentTarget.value, 'name')
                        }
                        applySearch={() => applySearch('name')}
                        searchString={searchState.name}
                        clearSearch={() => clearSearch('name')}
                      />
                    )}
                  </TableCell>
                  <TableCell id='active-table-head'>
                    <HeadTable
                      title='Last Name'
                      sort={handleSort}
                      filterHandle={filterDropdown}
                    />
                    {dropdown.last && (
                      <TablesSearch
                        search={(event) =>
                          search(event.currentTarget.value, 'surname')
                        }
                        applySearch={() => applySearch('surname')}
                        searchString={searchState.surname}
                        clearSearch={() => clearSearch('surname')}
                      />
                    )}
                  </TableCell>
                  <TableCell id='active-table-head'>
                    <HeadTable
                      title='Email address'
                      sort={handleSort}
                      filterHandle={filterDropdown}
                    />
                    {dropdown.email && (
                      <TablesSearch
                        search={(event) =>
                          search(event.currentTarget.value, 'email')
                        }
                        applySearch={() => applySearch('email')}
                        searchString={searchState.email}
                        clearSearch={() => clearSearch('email')}
                      />
                    )}
                  </TableCell>
                  <TableCell id='active-table-head'>
                    <HeadTable
                      title='Phone'
                      sort={handleSort}
                      filterHandle={filterDropdown}
                    />
                    {dropdown.phone && (
                      <TablesSearch
                        search={(event) =>
                          search(event.currentTarget.value, 'phone')
                        }
                        applySearch={() => applySearch('phone')}
                        searchString={searchState.phone}
                        clearSearch={() => clearSearch('phone')}
                      />
                    )}
                  </TableCell>
                  <TableCell id='status-table-head-actions'>
                    <HeadTable
                      title='Status'
                      sort={handleSort}
                      filterHandle={filterDropdown}
                    />
                    {dropdown.status && (
                      <DiscountFilter
                        checkDiscountFilter={checkBlockedFilter}
                        applyFilter={applyBlockedFilter}
                        clearSelectedItem={clearDiscountFilter}
                        withDiscount={blockedFilter.with}
                        withoutDiscount={blockedFilter.without}
                        firstCheckboxTitle='block'
                        secondCheckboxTitle='active'
                      />
                    )}
                  </TableCell>
                  <TableCell id='active-table-head-actions'>Actions</TableCell>
                </TableRow>
              </TableHead>
              {state.users.map((item) => {
                return (
                  <TableBody
                    key={item._id}
                    className={item._id === modal.id ? 'mark-user-row' : ''}
                    onClick={closeFilter}
                  >
                    <TableRow>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.surname}</TableCell>
                      <TableCell>{item.email}</TableCell>
                      <TableCell>{parsePhone(item.phone)}</TableCell>
                      <TableCell>
                        <Box display='flex' justifyContent='center'>
                          <label className='switch'>
                            <input
                              type='checkbox'
                              checked={!item.isBlocked}
                              onClick={() => changeStatus(item)}
                            />
                            <span className='slider round'></span>
                          </label>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <div className='actions-table-body'>
                          {checkPermission(
                            permissions,
                            'User management',
                            'View'
                          ) && (
                            <ShowButton
                              to={`users/${item.id}/show`}
                              id='show-dreamhome'
                            />
                          )}
                          {checkPermission(
                            permissions,
                            'User management',
                            'Update'
                          ) && (
                            <EditButton
                              to={`users/${item.id}`}
                              className='edit-prize'
                            />
                          )}
                          {checkPermission(
                            permissions,
                            'User management',
                            'Delete'
                          ) && (
                            <div onClick={() => openModal(item._id)}>
                              <DeleteForeverIcon className='delete-icon' />
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                );
              })}
            </Table>
          </TableContainer>
          <PaginationTable
            page={state.page}
            perPage={state.perPage}
            collectionLength={state.users.length}
            total={state.total}
            handleChangePage={handleChangePage}
            handleLastPage={handleLastPage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleFirstPage={handleFirstPage}
            title='Users'
          />
          <Dialog fullWidth open={modal.showModal} onClose={closeModal}>
            <DialogContent>
              <RemoveItem
                closeModal={closeModal}
                removeUser={removeUser}
                mainText='user account'
                minorText='All information about the user will be completely remove
                    from the system'
              />
            </DialogContent>
          </Dialog>
        </Card>
      </Box>
    </>
  );
};

export default UsersList;
